"use strict";
/*******************************************************************************

    Contains definition for the signature

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
exports.Signature = void 0;
var Utils_1 = require("../utils/Utils");
var ECC_1 = require("./ECC");
/**
 * Define the signature
 */
var Signature = /** @class */ (function () {
    /**
     * Construct a new instance of this class
     *
     * @param data   The string or binary representation of the Signature
     * @param endian The byte order
     * @throws Will throw an error if the data is not the same size as 64.
     */
    function Signature(data, endian) {
        if (endian === void 0) { endian = Utils_1.Endian.Big; }
        if (typeof data === "string")
            this.data = Utils_1.Utils.readFromString(data, Buffer.alloc(Signature.Width));
        else {
            this.data = Buffer.alloc(Signature.Width);
            this.fromBinary(data, endian);
        }
        if (this.data.length !== Signature.Width)
            throw new Error("The size of the data is abnormal.");
        this.R = new ECC_1.Point(this.data.slice(ECC_1.Scalar.Width));
        this.s = new ECC_1.Scalar(this.data.slice(0, ECC_1.Scalar.Width));
    }
    /**
     * Returns new instance of Signature from Scalar and Point
     * @param R The instance of Point
     * @param s The instance of Scalar
     */
    Signature.fromSchnorr = function (R, s) {
        return new Signature(Buffer.concat([s.data, R.data]));
    };
    /**
     * Reads from the hex string
     * @param hex The hex string
     * @returns The instance of Signature
     */
    Signature.prototype.fromString = function (hex) {
        Utils_1.Utils.readFromString(hex, this.data);
        return this;
    };
    /**
     * Writes to the hex string
     * @returns The hex string
     */
    Signature.prototype.toString = function () {
        return Utils_1.Utils.writeToString(this.data);
    };
    /**
     * Set binary data
     * @param bin    The binary data of the signature
     * @param endian The byte order
     * @returns The instance of Signature
     * @throws Will throw an error if the argument `bin` is not the same size as 64.
     */
    Signature.prototype.fromBinary = function (bin, endian) {
        if (endian === void 0) { endian = Utils_1.Endian.Big; }
        if (bin.length !== Signature.Width)
            throw new Error("The size of the data is abnormal.");
        bin.copy(this.data);
        if (endian === Utils_1.Endian.Little)
            this.data.reverse();
        return this;
    };
    /**
     * Get binary data
     * @param endian The byte order
     * @returns The binary data of the signature
     */
    Signature.prototype.toBinary = function (endian) {
        if (endian === void 0) { endian = Utils_1.Endian.Big; }
        if (endian === Utils_1.Endian.Little)
            return Buffer.from(this.data).reverse();
        else
            return this.data;
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    Signature.prototype.computeHash = function (buffer) {
        this.R.computeHash(buffer);
        this.s.computeHash(buffer);
    };
    /**
     * Converts this object to its JSON representation
     */
    Signature.prototype.toJSON = function () {
        return this.toString();
    };
    /**
     * Serialize as binary data.
     * @param buffer The buffer where serialized data is stored
     */
    Signature.prototype.serialize = function (buffer) {
        buffer.writeBuffer(this.R.data);
        buffer.writeBuffer(this.s.data);
    };
    /**
     * Deserialize as binary data.
     * @param buffer The buffer to be deserialized
     */
    Signature.deserialize = function (buffer) {
        var R = new ECC_1.Point(Utils_1.Utils.readBuffer(buffer, ECC_1.Point.Width));
        var s = new ECC_1.Scalar(Utils_1.Utils.readBuffer(buffer, ECC_1.Scalar.Width));
        return Signature.fromSchnorr(R, s);
    };
    /**
     * Returns the data size.
     */
    Signature.prototype.getNumberOfBytes = function () {
        return this.data.length;
    };
    /**
     * The number of byte of the signature
     */
    Signature.Width = 64;
    return Signature;
}());
exports.Signature = Signature;
//# sourceMappingURL=Signature.js.map